import { FunctionComponent } from 'react';
import toInteger from 'lodash/toInteger';
import { useQuery, gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Helmet } from 'react-helmet';
import { Profile as ProfileType } from '~types';

import ErrorBoundary from '../error/ErrorBoundary';
import NotFound from '../error/NotFound';
import { Loader } from '~components/Loader';
import { Items, GetTypeSocial } from '~components/profile';

import '../../scss/pages/_profile.scss';
import { ProfileRight } from '~frontend/components/profile/ProfileRight';
import { useHttpClient } from '~shared/frontend/providers/HttpProvider';

interface GetUser extends ProfileType {
  createdFormat?: any;
}

interface UserData {
  getUser: GetUser;
}

interface UserVars {
  id: number;
}

const User: FunctionComponent = () => {
  const { setHttpContext } = useHttpClient();
  const { id } = useParams();
  const { t } = useTranslation();

  const userId = toInteger(id);

  const { data, loading, error } = useQuery<UserData, UserVars>(
    gql`
      query User($id: Int!) {
        getUser(id: $id) {
          id
          userName
          userId
          avatar
          social
          createdAt
          createdFormat @client
        }
      }
    `,
    {
      variables: {
        id: userId,
      },
    },
  );

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>User | Error! {error.message}</>;
  }

  const getUser = data?.getUser;

  if (getUser === null) {
    setHttpContext({ redirect: '/' });
    return <NotFound />;
  }

  return (
    <>
      <Helmet>
        <title>
          {t('Profile userName on Steam and CSCASE', {
            userName: getUser.userName,
          })}
        </title>
        <meta
          name="description"
          content={t(
            'Profile of the user userName on CSCASE, see all openings of cs go cases at userName',
            {
              userName: getUser.userName,
            },
          )}
        />
      </Helmet>
      <div className="profile-page">
        <div className="page-profile page-profile-other">
          <h1 style={{ display: 'none' }}>
            {t('User_h1', {
              userName: getUser.userName,
            })}
          </h1>
          <div className="profile-wrap">
            <div className="profile-center">
              <div className="user-ava">
                <LazyLoadImage src={getUser.avatar} alt={getUser.userName} />
              </div>
              <div className="user-content">
                <div className="user-name">{getUser.userName}</div>
                <div className="user-soc-link">
                  <GetTypeSocial
                    userId={getUser.userId}
                    social={getUser.social}
                    userName={getUser.userName}
                  />
                </div>
              </div>
            </div>

            <ProfileRight userId={userId} isUser={true} />
          </div>
          <div className="profile-cases-wrap">
            <div className="in-case profile-cases profile-cases-user">
              <ErrorBoundary>
                <Items isUser={true} userId={userId} />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default User;
